/**
 * DEFCAT-R2-15 — auto-derive the per-axis structure levels from the actual
 * trees (decision OQ-R2-2a). Levels are a FACT of the trees, never an input:
 *
 *  - Location axis: L0 (the global component group, ALWAYS present as the root)
 *    + (max depth of catalogue location entries + 1) levels below it.
 *    A flat Nordex catalogue (L2 entries directly under L0) → 2 levels:
 *    [Component group, Location]. Enercon (L0→L1 group→L2) → 3 levels:
 *    [Component group, Location group, Location].
 *  - Type axis: (max depth of type entries + 1) levels. A FLAT type tree →
 *    exactly ONE level [Defect type] — the phantom "Type 1" disappears.
 *
 * The only editable thing per level is its LABEL; depth is derived. Labels are
 * persisted via PUT axis-levels and surface as Trees-tab level captions
 * (R2-04) and Add-button labels (R2-11). Pure + unit-tested.
 */
import type {
  CatalogueAxis,
  CatalogueAxisLevel,
  DefectCatalogueEntryV2,
  LocalizedString,
} from "@aeriosiq/types";
import { locText } from "./locText.js";

export interface DerivedLevel {
  axis: CatalogueAxis;
  /** 0-based depth within the axis (location L0 = 0). */
  level: number;
  /** Saved label, falling back to the sensible default for this slot. */
  label: string;
  /** True when no saved label exists for this slot (showing the default). */
  isDefault: boolean;
}

/** Sensible default labels per axis slot (OQ-R2-2 / video defaults). */
export function defaultLevelLabel(
  axis: CatalogueAxis,
  level: number,
  locationDepthCount: number,
): string {
  if (axis === "type") return "Defect type";
  // Location: L0 is always the component group. The DEEPEST location level is
  // "Location"; intermediate levels are "Location group". `locationDepthCount`
  // is the number of catalogue-location levels BELOW L0 (>= 1).
  if (level === 0) return "Component group";
  if (level === locationDepthCount) return "Location";
  return "Location group";
}

/** Max parent-chain depth (0-based) among an axis's entries, or -1 if none. */
function maxAxisDepth(
  entries: DefectCatalogueEntryV2[],
  axis: CatalogueAxis,
): number {
  const axisEntries = entries.filter((e) => e.axis === axis);
  if (axisEntries.length === 0) return -1;
  const byId = new Map<string, DefectCatalogueEntryV2>();
  for (const e of axisEntries) byId.set(e.entryId, e);
  function depthOf(e: DefectCatalogueEntryV2): number {
    let d = 0;
    const seen = new Set<string>();
    let cur: DefectCatalogueEntryV2 = e;
    while (!seen.has(cur.entryId)) {
      seen.add(cur.entryId);
      const pid: string | null | undefined = cur.parentEntryId;
      const parent: DefectCatalogueEntryV2 | undefined =
        pid != null && pid.length > 0 ? byId.get(pid) : undefined;
      if (!parent) break;
      d += 1;
      cur = parent;
    }
    return d;
  }
  let max = 0;
  for (const e of axisEntries) max = Math.max(max, depthOf(e));
  return max;
}

/** Build a saved-label lookup `axis:level → LocalizedString`. */
function savedLabelMap(
  levels: CatalogueAxisLevel[] | null | undefined,
): Map<string, LocalizedString> {
  const m = new Map<string, LocalizedString>();
  for (const l of levels ?? []) m.set(`${l.axis}:${String(l.level)}`, l.label);
  return m;
}

/**
 * Derive the full level structure for both axes from the trees. The location
 * axis ALWAYS starts with an L0 component-group level even when no location
 * entry carries one (the root is conceptually the component group). The type
 * axis derives purely from type-entry depth (>= 1 level whenever any type
 * exists; 0 levels only for a catalogue with no types at all).
 */
export function deriveAxisLevels(
  entries: DefectCatalogueEntryV2[],
  savedLevels: CatalogueAxisLevel[] | null | undefined,
): DerivedLevel[] {
  const saved = savedLabelMap(savedLevels);
  const out: DerivedLevel[] = [];

  // ── Location axis ──────────────────────────────────────────────────────
  const locDepth = maxAxisDepth(entries, "location"); // -1 if no loc entries
  // Catalogue-location levels below L0: depth 0..locDepth → (locDepth + 1)
  // levels; but L0 sits ABOVE them, so total location levels = locDepth + 2
  // (L0 + the catalogue levels). With no location entries at all we still show
  // L0 + one "Location" level so the structure is meaningful.
  const locationDepthCount = Math.max(1, locDepth + 1);
  const locationLevels = locationDepthCount + 1; // + L0
  for (let level = 0; level < locationLevels; level += 1) {
    const savedLabel = saved.get(`location:${String(level)}`);
    out.push({
      axis: "location",
      level,
      label: savedLabel
        ? locText(savedLabel, "")
        : defaultLevelLabel("location", level, locationDepthCount),
      isDefault: !savedLabel,
    });
  }

  // ── Type axis ──────────────────────────────────────────────────────────
  const typeDepth = maxAxisDepth(entries, "type"); // -1 if no type entries
  // Flat type tree (typeDepth === 0) → exactly ONE level. No types → 0 levels.
  const typeLevels = typeDepth < 0 ? 0 : typeDepth + 1;
  for (let level = 0; level < typeLevels; level += 1) {
    const savedLabel = saved.get(`type:${String(level)}`);
    out.push({
      axis: "type",
      level,
      label: savedLabel
        ? locText(savedLabel, "")
        : defaultLevelLabel("type", level, 0),
      isDefault: !savedLabel,
    });
  }

  return out;
}

/** Derived levels for one axis, in depth order. */
export function levelsForAxis(
  derived: DerivedLevel[],
  axis: CatalogueAxis,
): DerivedLevel[] {
  return derived
    .filter((d) => d.axis === axis)
    .sort((a, b) => a.level - b.level);
}
